/**
 * Maps Vector ToolCard edit tools to Terra Draw modes and select flags.
 * Pure so the adapter, the card, and unit tests share one contract.
 */
import type { IdentifyGeometryKind } from './vectorIdentifyModel';

export type VectorEditTool = 'draw' | 'modify' | 'scale' | 'delete' | 'trace';

export type TerraDrawModeName = 'point' | 'linestring' | 'polygon' | 'select';

export interface VectorEditSession {
  readonly terraMode: TerraDrawModeName;
  readonly snap: boolean;
  readonly scale: boolean;
}

export function drawKindForGeometry(kind: IdentifyGeometryKind): TerraDrawModeName {
  if (kind === 'line') return 'linestring';
  if (kind === 'polygon') return 'polygon';
  return 'point';
}

export function resolveVectorEditSession(
  tool: VectorEditTool,
  geometryKind: IdentifyGeometryKind,
): VectorEditSession | null {
  if (tool === 'delete') return null;
  if (tool === 'draw' || tool === 'trace') {
    return {
      terraMode: drawKindForGeometry(geometryKind),
      snap: tool === 'trace',
      scale: false,
    };
  }
  return { terraMode: 'select', snap: false, scale: tool === 'scale' };
}

export function terraModeFromGeometry(geometry: unknown): TerraDrawModeName {
  if (!geometry || typeof geometry !== 'object') return 'point';
  const type = (geometry as { type?: unknown }).type;
  if (type === 'LineString' || type === 'MultiLineString') return 'linestring';
  if (type === 'Polygon' || type === 'MultiPolygon') return 'polygon';
  return 'point';
}

export function editCursorForTool(tool: VectorEditTool | null): string {
  if (!tool || tool === 'delete') return '';
  if (tool === 'draw' || tool === 'trace') return 'crosshair';
  return 'grab';
}

/**
 * Overlay MapLibre canvases default to pointer-events:none so Giro3D
 * keeps OrbitControls. While a draw/edit tool is armed they must accept
 * pointer events or Terra Draw never sees a click.
 */
export function applyOverlayEditCapture(
  elements: ReadonlyArray<HTMLElement | null | undefined>,
  options: { readonly armed: boolean; readonly cursor: string; readonly overlay: boolean },
): void {
  const pointerEvents = options.armed || !options.overlay ? 'auto' : 'none';
  for (const element of elements) {
    if (!element) continue;
    if (options.overlay) element.style.pointerEvents = pointerEvents;
    element.style.cursor = options.cursor;
  }
}

export function terraSelectFlags(scale: boolean): Record<string, unknown> {
  const feature = { draggable: true, rotateable: scale, scaleable: scale };
  const coordinates = { midpoints: true, draggable: true, deletable: true };
  return {
    point: { feature: { draggable: true } },
    linestring: { feature, coordinates },
    polygon: { feature, coordinates },
  };
}
